import { API_BASE as BASE_URL, baseHeaders, getAuthHeaders, apiFetch } from './api-fetch';

export interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string;
  status: 'new' | 'contacted' | 'qualified' | 'proposal' | 'converted' | 'lost';
  score: number;
  source: string;
  propertyInterest?: string;
  budget?: number;
  notes?: string;
  assignedAgent?: string; // Agent ID or name
  assignedAgentEmail?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Segment {
  id: string;
  name: string;
  description: string;
  criteria: any;
  leadCount: number;
  createdAt: string;
}

export interface EmailCampaign {
  id: string;
  name: string;
  subject: string;
  content: string;
  status: 'draft' | 'scheduled' | 'active' | 'paused' | 'completed';
  segment?: string;
  scheduledDate?: string;
  templateId?: string; // Add template reference
  sentCount: number;
  openRate: number;
  clickRate: number;
  createdAt: string;
}

export interface SocialCampaign {
  id: string;
  name: string;
  content: string;
  platforms: string[];
  status: 'draft' | 'scheduled' | 'published' | 'completed';
  scheduledDate?: string;
  propertyId?: string;
  engagement: {
    likes: number;
    shares: number;
    comments: number;
    reach: number;
  };
  createdAt: string;
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  content: string;
  category: 'welcome' | 'follow-up' | 'property-alert' | 'newsletter' | 'promotional' | 'custom';
  thumbnail?: string;
  variables: string[]; // e.g., ['firstName', 'propertyName', 'price']
  createdAt: string;
  updatedAt: string;
}

export interface WhatsAppTemplate {
  id: string;
  name: string;
  content: string;
  category: 'welcome' | 'follow-up' | 'property-alert' | 'newsletter' | 'promotional' | 'custom';
  variables: string[]; // e.g., ['firstName', 'propertyName', 'price']
  createdAt: string;
  updatedAt: string;
}

export interface AutomatedEmail {
  id: string;
  name: string;
  type: 'drip-sequence' | 'trigger-based';
  status: 'active' | 'paused' | 'draft';
  trigger?: {
    event: 'lead-created' | 'lead-qualified' | 'property-viewed' | 'days-after-signup';
    daysDelay?: number;
    criteria?: any;
  };
  sequence?: {
    step: number;
    channel: 'email' | 'whatsapp'; // Added channel selector
    templateId: string;
    delayDays: number;
  }[];
  stats: {
    sent: number;
    opened: number;
    clicked: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface MarketingAnalytics {
  leads: {
    total: number;
    new: number;
    qualified: number;
    converted: number;
    avgScore: string;
  };
  emailCampaigns: {
    total: number;
    active: number;
    totalSent: number;
    avgOpenRate: string;
    avgClickRate: string;
  };
  socialCampaigns: {
    total: number;
    active: number;
    totalReach: number;
    totalEngagement: number;
  };
}

export const marketingAPI = {
  // ============ LEADS ============
  
  async getLeads(): Promise<{ leads: Lead[] }> {
    const response = await apiFetch(`${BASE_URL}/marketing/leads`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch leads');
    return response.json();
  },

  async createLead(lead: Partial<Lead>): Promise<{ success: boolean; lead: Lead }> {
    const response = await apiFetch(`${BASE_URL}/marketing/leads`, {
      method: 'POST',
      headers: baseHeaders,
      body: JSON.stringify(lead)
    });
    if (!response.ok) throw new Error('Failed to create lead');
    return response.json();
  },

  async updateLead(id: string, updates: Partial<Lead>): Promise<{ success: boolean; lead: Lead }> {
    const response = await apiFetch(`${BASE_URL}/marketing/leads/${id}`, {
      method: 'PUT',
      headers: baseHeaders,
      body: JSON.stringify(updates)
    });
    if (!response.ok) throw new Error('Failed to update lead');
    return response.json();
  },

  async deleteLead(id: string): Promise<{ success: boolean }> {
    const response = await apiFetch(`${BASE_URL}/marketing/leads/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete lead');
    return response.json();
  },

  // ============ SEGMENTS ============

  async getSegments(): Promise<{ segments: Segment[] }> {
    const response = await apiFetch(`${BASE_URL}/marketing/segments`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch segments');
    return response.json();
  },

  async createSegment(segment: Partial<Segment>): Promise<{ success: boolean; segment: Segment }> {
    const response = await apiFetch(`${BASE_URL}/marketing/segments`, {
      method: 'POST',
      headers: baseHeaders,
      body: JSON.stringify(segment)
    });
    if (!response.ok) throw new Error('Failed to create segment');
    return response.json();
  },

  // ============ EMAIL CAMPAIGNS ============

  async getEmailCampaigns(): Promise<{ campaigns: EmailCampaign[] }> {
    const response = await apiFetch(`${BASE_URL}/marketing/campaigns/email`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch email campaigns');
    return response.json();
  },

  async createEmailCampaign(campaign: Partial<EmailCampaign>): Promise<{ success: boolean; campaign: EmailCampaign }> {
    const response = await apiFetch(`${BASE_URL}/marketing/campaigns/email`, {
      method: 'POST',
      headers: baseHeaders,
      body: JSON.stringify(campaign)
    });
    if (!response.ok) throw new Error('Failed to create email campaign');
    return response.json();
  },

  async updateEmailCampaign(id: string, updates: Partial<EmailCampaign>): Promise<{ success: boolean; campaign: EmailCampaign }> {
    const response = await apiFetch(`${BASE_URL}/marketing/campaigns/email/${id}`, {
      method: 'PUT',
      headers: baseHeaders,
      body: JSON.stringify(updates)
    });
    if (!response.ok) throw new Error('Failed to update email campaign');
    return response.json();
  },

  async deleteEmailCampaign(id: string): Promise<{ success: boolean }> {
    const response = await apiFetch(`${BASE_URL}/marketing/campaigns/email/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete email campaign');
    return response.json();
  },

  // ============ SOCIAL CAMPAIGNS ============

  async getSocialCampaigns(): Promise<{ campaigns: SocialCampaign[] }> {
    const response = await apiFetch(`${BASE_URL}/marketing/campaigns/social`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch social campaigns');
    return response.json();
  },

  async createSocialCampaign(campaign: Partial<SocialCampaign>): Promise<{ success: boolean; campaign: SocialCampaign }> {
    const response = await apiFetch(`${BASE_URL}/marketing/campaigns/social`, {
      method: 'POST',
      headers: baseHeaders,
      body: JSON.stringify(campaign)
    });
    if (!response.ok) throw new Error('Failed to create social campaign');
    return response.json();
  },

  async updateSocialCampaign(id: string, updates: Partial<SocialCampaign>): Promise<{ success: boolean; campaign: SocialCampaign }> {
    const response = await apiFetch(`${BASE_URL}/marketing/campaigns/social/${id}`, {
      method: 'PUT',
      headers: baseHeaders,
      body: JSON.stringify(updates)
    });
    if (!response.ok) throw new Error('Failed to update social campaign');
    return response.json();
  },

  async deleteSocialCampaign(id: string): Promise<{ success: boolean }> {
    const response = await apiFetch(`${BASE_URL}/marketing/campaigns/social/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete social campaign');
    return response.json();
  },

  // ============ EMAIL TEMPLATES ============

  async getEmailTemplates(): Promise<{ templates: EmailTemplate[] }> {
    const response = await apiFetch(`${BASE_URL}/marketing/templates/email`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch email templates');
    return response.json();
  },

  async createEmailTemplate(template: Partial<EmailTemplate>): Promise<{ success: boolean; template: EmailTemplate }> {
    const response = await apiFetch(`${BASE_URL}/marketing/templates/email`, {
      method: 'POST',
      headers: baseHeaders,
      body: JSON.stringify(template)
    });
    if (!response.ok) throw new Error('Failed to create email template');
    return response.json();
  },

  async updateEmailTemplate(id: string, updates: Partial<EmailTemplate>): Promise<{ success: boolean; template: EmailTemplate }> {
    const response = await apiFetch(`${BASE_URL}/marketing/templates/email/${id}`, {
      method: 'PUT',
      headers: baseHeaders,
      body: JSON.stringify(updates)
    });
    if (!response.ok) throw new Error('Failed to update email template');
    return response.json();
  },

  async deleteEmailTemplate(id: string): Promise<{ success: boolean }> {
    const response = await apiFetch(`${BASE_URL}/marketing/templates/email/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete email template');
    return response.json();
  },

  // ============ WHATSAPP TEMPLATES ============

  async getWhatsAppTemplates(): Promise<{ templates: WhatsAppTemplate[] }> {
    const response = await apiFetch(`${BASE_URL}/marketing/templates/whatsapp`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch WhatsApp templates');
    return response.json();
  },

  async createWhatsAppTemplate(template: Partial<WhatsAppTemplate>): Promise<{ success: boolean; template: WhatsAppTemplate }> {
    const response = await apiFetch(`${BASE_URL}/marketing/templates/whatsapp`, {
      method: 'POST',
      headers: baseHeaders,
      body: JSON.stringify(template)
    });
    if (!response.ok) throw new Error('Failed to create WhatsApp template');
    return response.json();
  },

  async updateWhatsAppTemplate(id: string, updates: Partial<WhatsAppTemplate>): Promise<{ success: boolean; template: WhatsAppTemplate }> {
    const response = await apiFetch(`${BASE_URL}/marketing/templates/whatsapp/${id}`, {
      method: 'PUT',
      headers: baseHeaders,
      body: JSON.stringify(updates)
    });
    if (!response.ok) throw new Error('Failed to update WhatsApp template');
    return response.json();
  },

  async deleteWhatsAppTemplate(id: string): Promise<{ success: boolean }> {
    const response = await apiFetch(`${BASE_URL}/marketing/templates/whatsapp/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete WhatsApp template');
    return response.json();
  },

  // ============ AUTOMATED EMAILS ============

  async getAutomatedEmails(): Promise<{ emails: AutomatedEmail[] }> {
    const response = await apiFetch(`${BASE_URL}/marketing/automated-emails`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch automated emails');
    return response.json();
  },

  async createAutomatedEmail(email: Partial<AutomatedEmail>): Promise<{ success: boolean; email: AutomatedEmail }> {
    const response = await apiFetch(`${BASE_URL}/marketing/automated-emails`, {
      method: 'POST',
      headers: baseHeaders,
      body: JSON.stringify(email)
    });
    if (!response.ok) throw new Error('Failed to create automated email');
    return response.json();
  },

  async updateAutomatedEmail(id: string, updates: Partial<AutomatedEmail>): Promise<{ success: boolean; email: AutomatedEmail }> {
    const response = await apiFetch(`${BASE_URL}/marketing/automated-emails/${id}`, {
      method: 'PUT',
      headers: baseHeaders,
      body: JSON.stringify(updates)
    });
    if (!response.ok) throw new Error('Failed to update automated email');
    return response.json();
  },

  async deleteAutomatedEmail(id: string): Promise<{ success: boolean }> {
    const response = await apiFetch(`${BASE_URL}/marketing/automated-emails/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete automated email');
    return response.json();
  },

  // ============ CONVENIENCE ALIASES ============
  // These aliases match what AdminEmailManagement expects

  async getCampaigns(): Promise<{ campaigns: EmailCampaign[] }> {
    return this.getEmailCampaigns();
  },

  async createCampaign(campaign: Partial<EmailCampaign>): Promise<{ success: boolean; campaign: EmailCampaign }> {
    return this.createEmailCampaign(campaign);
  },

  async updateCampaign(id: string, updates: Partial<EmailCampaign>): Promise<{ success: boolean; campaign: EmailCampaign }> {
    return this.updateEmailCampaign(id, updates);
  },

  // ============ ANALYTICS ============

  async getAnalytics(): Promise<{ analytics: MarketingAnalytics }> {
    const response = await apiFetch(`${BASE_URL}/marketing/analytics`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch analytics');
    return response.json();
  }
};
